import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Alert
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Send as SendIcon
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import s3Utils from '../../../utils/s3Utils';
import { generateDecisionLetter } from "../../../components/certificate/DecisionLetterGenerator";
import dayjs from "dayjs";
import { encryptAppId, encryptText } from '../../../utils/encryption';



const AdditionalAppDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('New');
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingAccept, setacceptLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [forwardLoading, setForwardLoading] = useState(false);
  const [counts, setCounts] = useState({
    New: 0,
    Accepted: 0,
    Rejected: 0,
    Forwarded: 0
  });

  // Modal states
  const [acceptDialogOpen, setAcceptDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [forwardDialogOpen, setForwardDialogOpen] = useState(false);
  const [decisionDialogOpen, setDecisionDialogOpen] = useState(false);
  const [trailsDialogOpen, setTrailsDialogOpen] = useState(false);

  // Form states
  const [selectedApp, setSelectedApp] = useState(null);
  const [acceptPassword, setAcceptPassword] = useState('');
  const [rejectPassword, setRejectPassword] = useState('');
  const [rejectRemarks, setRejectRemarks] = useState('');
  const [forwardPassword, setForwardPassword] = useState('');
  const [selectedDirector, setSelectedDirector] = useState('0');
  const [directorsList, setDirectorsList] = useState([]);
  const [decisionBody, setDecisionBody] = useState('');
  const [decisionTrails, setDecisionTrails] = useState([]);

  useEffect(() => {
    loadApplications();
    loadAllCounts();
  }, [activeTab]);

  const loadApplications = async () => {
    try {
      setLoading(true);
      const orgId = localStorage.getItem('orgId');
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/final-submit-additional/jd/applications`,
        {
          params: {
            orgid: orgId,
            appType: activeTab
          }
        }
      );

      if (response.data.success) {
        setApplications(response.data.data);
      }
    } catch (error) {
      console.error('Error loading applications:', error);
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const loadAllCounts = async () => {
    try {
      const orgId = localStorage.getItem('orgId');
      const tabs = ['New', 'Accepted', 'Rejected', 'Forwarded'];
      
      const promises = tabs.map(tab =>
        axios.get(
          `${process.env.REACT_APP_API_URL}/final-submit-additional/jd/applications`,
          {
            params: {
              orgid: orgId,
              appType: tab
            }
          }
        )
      );

      const results = await Promise.all(promises);
      const newCounts = {};
      
      results.forEach((result, index) => {
        newCounts[tabs[index]] = result.data.data?.length || 0;
      });

      setCounts(newCounts);
    } catch (error) {
      console.error('Error loading counts:', error);
    }
  };

  const loadDirectors = async (appid) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/ceolist`,
        {
          params: {
            appid: appid,
            usertypeid: 8 // Director user type
          }
        }
      );

      if (response.data && response.data.ceo) {
        setDirectorsList(response.data.ceo);
      }
    } catch (error) {
      console.error('Error loading directors:', error);
      toast.error('Failed to load directors list');
    }
  };

  const handleViewFile = async (filePath) => {
      if (!filePath) {
        toast.error("File not found");
        return;
      }
      try {
        setLoading(true);
        // Use s3Utils to handle file opening with signed URLs
        await s3Utils.handleFileViewWithS3(filePath, {
          showError: (msg) => toast.error(msg),
          showLoading: () => toast.info("Loading..."),
          hideLoading: () => toast.dismiss(),
          expiresIn: 172800, // 48 hours
        });
      } catch (err) {
        toast.error("Failed to open file");
        console.error("Error opening file:", err);
      } finally {
        toast.dismiss();
      }
    };

  const handleAcceptClick = (app) => {
    setSelectedApp(app);
    setAcceptDialogOpen(true);
  };

  const handleRejectClick = (app) => {
    setSelectedApp(app);
    setRejectDialogOpen(true);
  };

  const handleForwardClick = (app) => {
    setSelectedApp(app);
    setForwardDialogOpen(true);
    loadDirectors(app.appid);
  };

  const handleViewDecision = (app) => {
    handleViewDecisionLetter(app);
    // if (app.chkletter === 1) {
    //   setDecisionBody(app.Body);
    //   setDecisionDialogOpen(true);
    // } else {
    //   handleViewDecisionLetter
    // }
  };

  // Handle View Decision Letter - Generate PDF
    const handleViewDecisionLetter = async (data) => {
      try {
        setLoading(true);
        toast.info("Generating decision letter...");
  
        const letterData = {
          applicationNo: data.ApplicationNo || "N/A",
          letterDate: data.DecisionDate || "-",
          contactPerson: data.ContactPerson || "N/A",
          laboratoryName: data.LabName || "N/A",
          address: data.Address || "N/A",
          extAddress: data.Extaddress || "N/A",
          mobile: data.ContactMobile || "N/A",
          email: data.ContactEmail || "N/A",
          subject: data.Subject || "N/A",
          body: data.body || data.Body || "N/A",
          doName: data.Name || "N/A",
          doEmail: data.doEmail || "N/A",
          showNewAddress: new Date() > new Date("2025-07-18"),
        };
  
        await generateDecisionLetter(letterData);
  
        toast.success("Decision letter downloaded successfully");
      } catch (err) {
        console.error("Error generating decision letter:", err);
        toast.error("Failed to generate decision letter");
      } finally {
        toast.dismiss();
      }
    };

  const handleViewTrails = async (appid) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/final-submit-additional/jd/decision-trails/${appid}`
      );

      if (response.data.success) {
        setDecisionTrails(response.data.data);
        setTrailsDialogOpen(true);
      }
    } catch (error) {
      console.error('Error loading trails:', error);
      toast.error('Failed to load decision trails');
    }
  };

  const fetchDecisionProceedThrough = async (appId) => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/final-submit-additional/reissue-certificate/${appId}`,
    );

    if (response.data.success && response.data.data) {
      const data = response.data.data[0];
      return data.DecisionProceedThrough;
    }
    return null;
  } catch (error) {
    return null;
  }
};

  const handleAcceptSubmit = async () => {
    if (!acceptPassword.trim()) {
      toast.warning('Please enter your password');
      return;
    }

    try {
      setacceptLoading(true);
      const storageId = localStorage.getItem('orgId');

      // Verify password
      const passwordResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/verify-password`,
        {
          orgId: storageId,
          password: acceptPassword
        }
      );

      if (!passwordResponse.data.success) {
        toast.error('Invalid password');
        return;
      }
      const decisionProceedThrough = await fetchDecisionProceedThrough(selectedApp.appid);
      // Accept decision
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/final-submit-additional/jd/accept`,
        {
          id: selectedApp.id,
          IsApproved: 1,
          orgid: storageId,
          Remarks: '',
          role: 9,
          decisionProceedThrough: decisionProceedThrough,
          appid: selectedApp.appid
        }
      );

      if (response.data.success) {
        toast.success('Application accepted successfully');
        setAcceptDialogOpen(false);
        setAcceptPassword('');
        loadApplications();
        loadAllCounts();
      }
    } catch (error) {
      console.error('Error accepting application:', error);
      toast.error('Failed to accept application');
    } finally {
      setacceptLoading(false);
    }
  };

  const handleRejectSubmit = async () => {
    if (!rejectPassword.trim()) {
      toast.warning('Please enter your password');
      return;
    }

    if (!rejectRemarks.trim()) {
      toast.warning('Please enter reject remarks');
      return;
    }

    try {
      setRejectLoading(true);
      const storageId = localStorage.getItem('orgId');

      // Verify password
      const passwordResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/verify-password`,
        {
          orgId: storageId,
          password: rejectPassword
        }
      );

      if (!passwordResponse.data.success) {
        toast.error('Invalid password');
        return;
      }

      // Reject decision
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/final-submit-additional/jd/reject`,
        {
          id: selectedApp.id,
          IsApproved: 2,
          orgid: storageId,
          Remarks: rejectRemarks,
          role: 9,
          Type: ""
        }
      );

      if (response.data.success) {
        toast.success('Application rejected successfully');
        setRejectDialogOpen(false);
        setRejectPassword('');
        setRejectRemarks('');
        loadApplications();
        loadAllCounts();
      }
    } catch (error) {
      console.error('Error rejecting application:', error);
      toast.error('Failed to reject application');
    } finally {
      setRejectLoading(false);
    }
  };

  const handleForwardSubmit = async () => {
    if (selectedDirector === '0') {
      toast.warning('Please select a Director');
      return;
    }

    if (!forwardPassword.trim()) {
      toast.warning('Please enter your password');
      return;
    }

    try {
      setForwardLoading(true);
      const storageId = localStorage.getItem('orgId');

      // Verify password
      const passwordResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/verify-password`,
        {
          orgId: storageId,
          password: forwardPassword
        }
      );

      if (!passwordResponse.data.success) {
        toast.error('Invalid password');
        return;
      }

      // Forward to director
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/final-submit-additional/jd/forward-to-director`,
        {
          id: selectedApp.id,
          sentto: selectedDirector,
          orgid: storageId
        }
      );

      if (response.data.success) {
        toast.success('Application forwarded to Director successfully');
        setForwardDialogOpen(false);
        setForwardPassword('');
        setSelectedDirector('0');
        loadApplications();
        loadAllCounts();
      }
    } catch (error) {
      console.error('Error forwarding application:', error);
      toast.error('Failed to forward application');
    } finally {
      setForwardLoading(false);
    }
  };

  const handleViewReports = (appid, orgId) => {
    const encryptedAppIdParam = encryptAppId(appid);
    const encryptedOrgIdParam = encryptText(orgId);
    const encryptedObservation = encryptText("false");
    const url = `/main/preview-report?appId=${encryptedAppIdParam}&orgId=${encryptedOrgIdParam}&observation=${encryptedObservation}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 3, color: '#00024c', fontWeight: 600 }}>
        Other Activities during the accreditation cycle (Till Validity) Decision Approval
      </Typography>

      {/* Tab Buttons */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
        {['New', 'Accepted', 'Rejected', 'Forwarded'].map((tab) => (
          <Button
            key={tab}
            variant={activeTab === tab ? 'contained' : 'outlined'}
            onClick={() => setActiveTab(tab)}
            sx={{ height: 50, px: 3 }}
          >
            {tab} Applications ({counts[tab]})
          </Button>
        ))}
      </Box>

      {/* Applications Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Sr. No.</TableCell>
              <TableCell>Lab Id</TableCell>
              <TableCell>Username</TableCell>
              <TableCell>Forward By</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Dealing Officer Decision</TableCell>
              <TableCell align="center">View Decision</TableCell>
              <TableCell align="center">View Reports</TableCell>
              <TableCell>Rejected Remarks</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {applications.map((app, index) => (
              <TableRow key={app.id}>
                <TableCell>
                  {index + 1}.
                  {app.VisBtn === 1 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5 }}>
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        startIcon={<CheckCircleIcon />}
                        onClick={() => handleAcceptClick(app)}
                      >
                        Accept
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        startIcon={<CancelIcon />}
                        onClick={() => handleRejectClick(app)}
                      >
                        Reject
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<SendIcon />}
                        onClick={() => handleForwardClick(app)}
                      >
                        Forward
                      </Button>
                    </Box>
                  )}
                </TableCell>
                <TableCell>{app.ApplicationNo}</TableCell>
                <TableCell>{app.LoginName}</TableCell>
                <TableCell>{app.Name}</TableCell>
                <TableCell>{app.Category}</TableCell>
                <TableCell>
                  {/* {app.Vistrail === 0 ? (
                    app.dostatus
                  ) : ( */}
                    <IconButton
                      color="primary"
                      onClick={() => handleViewTrails(app.appid)}
                      size="small"
                    >
                      <VisibilityIcon />
                    </IconButton>
                  {/* )} */}
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    color="primary"
                    onClick={() => handleViewDecision(app)}
                    size="small"
                  >
                    <VisibilityIcon />
                  </IconButton>
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    color="primary"
                    onClick={() => handleViewReports(app.appid, app.OrgId)}
                    size="small"
                  >
                    <VisibilityIcon />
                  </IconButton>
                </TableCell>
                <TableCell>{app.ApproveRemarks}</TableCell>
              </TableRow>
            ))}
            {applications.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No applications found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Accept Dialog */}
      <Dialog open={acceptDialogOpen} onClose={() => setAcceptDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Accept Application</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            type="password"
            label="Enter Your Password to Continue"
            value={acceptPassword}
            onChange={(e) => setAcceptPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAcceptDialogOpen(false)} color="error">
            Cancel
          </Button>
          <Button onClick={handleAcceptSubmit} variant="contained" disabled={loadingAccept}>
            {loadingAccept ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Application</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Enter Reject Remarks"
            value={rejectRemarks}
            onChange={(e) => setRejectRemarks(e.target.value)}
            multiline
            rows={3}
          />
          <TextField
            fullWidth
            margin="normal"
            type="password"
            label="Enter Your Password to Continue"
            value={rejectPassword}
            onChange={(e) => setRejectPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)} color="error">
            Cancel
          </Button>
          <Button onClick={handleRejectSubmit} variant="contained" disabled={rejectLoading}>
            {rejectLoading ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Forward Dialog */}
      <Dialog open={forwardDialogOpen} onClose={() => setForwardDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Forward To Director</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Select Director</InputLabel>
            <Select
              value={selectedDirector}
              onChange={(e) => setSelectedDirector(e.target.value)}
              label="Select Director"
            >
              <MenuItem value="0">--Select Director--</MenuItem>
              {directorsList.map((director) => (
                <MenuItem key={director.id} value={director.id}>
                  {director.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            margin="normal"
            type="password"
            label="Enter Your Password to Continue"
            value={forwardPassword}
            onChange={(e) => setForwardPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForwardDialogOpen(false)} color="error">
            Cancel
          </Button>
          <Button onClick={handleForwardSubmit} variant="contained" disabled={forwardLoading}>
            {forwardLoading ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Decision Dialog */}
      <Dialog open={decisionDialogOpen} onClose={() => setDecisionDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Decision</DialogTitle>
        <DialogContent>
          <Box sx={{ p: 2 }}>
            <Typography variant="body1" dangerouslySetInnerHTML={{ __html: decisionBody }} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDecisionDialogOpen(false)} color="error">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Trails Dialog */}
      <Dialog open={trailsDialogOpen} onClose={() => setTrailsDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Decision History</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>S No.</TableCell>
                  <TableCell>Expert Comments</TableCell>
                  <TableCell>Re-issue the Accreditation Certificate</TableCell>
                  <TableCell>Decision Process Through</TableCell>
                  <TableCell>Expert Comments on Decision</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {decisionTrails.map((trail, index) => (
                  <TableRow key={index}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      {trail.ExpertComment && trail.ExpertComment !== 'N/A' ? (
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => handleViewFile(trail.ExpertComment)}
                        >
                          View
                        </Button>
                      ) : (
                        'N/A'
                      )}
                    </TableCell>
                    <TableCell>{trail.Is_Re_IssueCertificate}</TableCell>
                    <TableCell>{trail.DecisionProceedThrough}</TableCell>
                    <TableCell>
                      {trail.ExpertCommentFile && trail.ExpertCommentFile !== 'N/A' ? (
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => handleViewFile(trail.ExpertCommentFile)}
                        >
                          View
                        </Button>
                      ) : (
                        'N/A'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTrailsDialogOpen(false)} color="error">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdditionalAppDashboard;
